import React, { createContext, useState, useContext, useMemo, useEffect, useCallback } from "react";
import ApiDocsLayout from "../components/ApiDocsLayout";

const ApiDocsContext = createContext();

export function useApiDocsContext() {
  return useContext(ApiDocsContext);
}

const ApiDocsProvider = ({ apiDocs, isAdmin }) => {
  const [selectedEndpointId, setSelectedEndpointId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const visibleCategories = useMemo(() => {
    if (isAdmin) return apiDocs.categories;
    return apiDocs.categories.filter((cat) => !cat.admin);
  }, [apiDocs.categories, isAdmin]);

  const allEndpoints = useMemo(() => {
    return visibleCategories.flatMap((cat) =>
      (cat.endpoints || []).map((ep) => ({
        ...ep,
        categoryName: cat.name,
        categoryFile: cat.file,
        isAdmin: cat.admin || false,
        isPublicApi: cat.public_api || false,
      }))
    );
  }, [visibleCategories]);

  const filteredEndpoints = useMemo(() => {
    if (!searchTerm.trim()) return allEndpoints;
    const term = searchTerm.toLowerCase();
    return allEndpoints.filter(
      (ep) =>
        ep.summary.toLowerCase().includes(term) ||
        ep.path.toLowerCase().includes(term) ||
        ep.method.toLowerCase().includes(term) ||
        (ep.description || "").toLowerCase().includes(term)
    );
  }, [allEndpoints, searchTerm]);

  const filteredCategories = useMemo(() => {
    if (!searchTerm.trim()) return visibleCategories;
    const filteredIds = new Set(filteredEndpoints.map((ep) => ep.id));
    return visibleCategories
      .map((cat) => ({
        ...cat,
        endpoints: (cat.endpoints || []).filter((ep) => filteredIds.has(ep.id)),
      }))
      .filter((cat) => cat.endpoints && cat.endpoints.length > 0);
  }, [visibleCategories, filteredEndpoints, searchTerm]);

  const selectedEndpoint = useMemo(() => {
    return allEndpoints.find((ep) => ep.id === selectedEndpointId);
  }, [allEndpoints, selectedEndpointId]);

  const handleSelectEndpoint = useCallback((endpointId) => {
    setSelectedEndpointId(endpointId);
    setSidebarOpen(false);
    window.history.replaceState(null, "", `#${endpointId}`);
  }, []);

  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (hash && allEndpoints.find((ep) => ep.id === hash)) {
      setSelectedEndpointId(hash);
    }
  }, [allEndpoints]);

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1);
      if (hash && allEndpoints.find((ep) => ep.id === hash)) {
        setSelectedEndpointId(hash);
      }
    };
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [allEndpoints]);

  const toggleCategory = useCallback((categoryFile) => {
    setExpandedCategories((prev) =>
      prev.includes(categoryFile)
        ? prev.filter((f) => f !== categoryFile)
        : [...prev, categoryFile]
    );
  }, []);

  const value = {
    apiDocs,
    selectedEndpointId,
    setSelectedEndpointId: handleSelectEndpoint,
    searchTerm,
    setSearchTerm,
    expandedCategories,
    toggleCategory,
    allEndpoints,
    filteredEndpoints,
    filteredCategories,
    selectedEndpoint,
    sidebarOpen,
    setSidebarOpen,
  };

  return (
    <ApiDocsContext.Provider value={value}>
      <ApiDocsLayout />
    </ApiDocsContext.Provider>
  );
};

const ApiDocsApp = (props) => <ApiDocsProvider {...props} />;

export default ApiDocsApp;
